import { useDetailsDrawer } from '@src/components/DetailsDrawer/useDetailsDrawer';
import { prime } from '@src/components/OMS/ManualTradeView/ManualTradeSlice';
import { openView } from '@src/components/OMS/OMSSlice';
import { OMSView } from '@src/components/OMS/OMSView';
import { getTradeModifyEligibility } from '@src/containers/Blotters/Trades/tradeModifyEligibility';
import { useFeatureFlag, useRoleAuth } from '@src/hooks';
import { useAppStateDispatch } from '@src/providers/AppStateProvider';
import {
  ACTION,
  Button,
  ButtonVariants,
  MixpanelEvent,
  MobileModalSheetActionsContainer,
  Order,
  TradeStatusEnum,
  useDynamicCallback,
  useMixpanel,
  useSecuritiesContext,
  type Trade,
} from '@talos/kyoko';

interface MobileTradeDetailFooterProps {
  trade: Trade;
}

/**
 * Action buttons rendered in the sticky footer of the mobile trade detail sheet.
 *
 * Modify mirrors the desktop `useTradeMenu` Modify entry — it primes the trade
 * into the manual trade form and opens the `ManualTradeForm` OMS view.
 *
 * Order Details opens the {@link OMSView.DetailsDrawer} view (which renders
 * `OrderAnalytics` for the trade's parent order). Disabled when the trade has
 * no `OrderID` (e.g. orphan / manual-booked trades without a linked order).
 */
export function MobileTradeDetailFooter({ trade }: MobileTradeDetailFooterProps) {
  const mixpanel = useMixpanel();
  const dispatch = useAppStateDispatch();
  const { openDetailsDrawer } = useDetailsDrawer();
  const { isAuthorized } = useRoleAuth();
  const { securitiesBySymbol } = useSecuritiesContext();
  const { enableGovernmentBonds, enableShares } = useFeatureFlag();

  const isCanceled = trade.TradeStatus === TradeStatusEnum.Canceled;
  const modifyEligibility = getTradeModifyEligibility(trade, securitiesBySymbol, {
    enableGovernmentBonds,
    enableShares,
  });
  // Mirrors `useTradeMenu`: manual-booked trades are always modifiable, regular
  // trades require ACTION.EDIT_TRADE. Canceled trades and product types that are
  // not Manual Trade eligible (OTC / STS digital, or Share / GovernmentBond with
  // their flags off) are never modifiable.
  const canModify =
    !isCanceled && (trade.isManualBookedTrade || isAuthorized(ACTION.EDIT_TRADE)) && modifyEligibility.modifiable;
  const canOpenOrderDetails = trade.OrderID != null;

  const handleModify = useDynamicCallback(() => {
    mixpanel.track(trade.isManualBookedTrade ? MixpanelEvent.ModifyManualTrade : MixpanelEvent.ModifyOrphanTrade);
    dispatch(prime(trade));
    dispatch(openView(OMSView.ManualTradeForm));
  });

  const handleOpenOrderDetails = useDynamicCallback(() => {
    if (trade.OrderID == null) {
      return;
    }
    mixpanel.track(MixpanelEvent.OpenOrderAnalytics);
    openDetailsDrawer({ entity: new Order({ OrderID: trade.OrderID } as Order) });
  });

  return (
    <MobileModalSheetActionsContainer>
      <Button
        data-testid="mobile-trade-detail-modify-button"
        pill
        flex={1}
        variant={ButtonVariants.Default}
        disabled={!canModify}
        title={modifyEligibility.reason}
        onClick={handleModify}
      >
        Modify
      </Button>
      <Button
        data-testid="mobile-trade-detail-order-details-button"
        pill
        flex={1}
        variant={ButtonVariants.Default}
        disabled={!canOpenOrderDetails}
        onClick={handleOpenOrderDetails}
      >
        Order Details
      </Button>
    </MobileModalSheetActionsContainer>
  );
}
